import React, { useState, useEffect, useRef } from "react";
import {Modal, Button, Collapse, Badge} from "react-bootstrap";
import {MdAutoAwesome, MdKeyboardArrowDown, MdKeyboardArrowUp, MdLabel} from "react-icons/md";
import "./AICallAnalysisModal.css";
import moment from "moment";
import {getGreekCurrencyFormat} from "../../../../_helpers/commonFunctions";

const AICallAnalysisModal = ({ show, onHide, data, analysisDate, cost, isLoading, row }) => {
    const [showChat, setShowChat] = useState(false);
    const [showAnalysis, setShowAnalysis] = useState(true);
    const chatTopRef = useRef(null);

    useEffect(() => {
        if (!show || isLoading) {
            setShowChat(false);
            setShowAnalysis(true);
        }
    }, [show, isLoading]);

    const handleToggleChat = () => {
        if (!showChat) {
            setShowChat(true);
            setShowAnalysis(false);
            setTimeout(() => {
                chatTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
        } else {
            setShowChat(false);
            setShowAnalysis(true);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered scrollable dialogClassName="modal65PercentWidth">
            <Modal.Header closeButton className="ai-modal-header">
                <Modal.Title className="w-100 text-center">
                    <div className="ai-header-content">
                        <div className="ai-subject-tag">
                            <MdAutoAwesome size={16} />
                            ΑΝΑΛΥΣΗ AI
                        </div>
                        <div className="ai-subject-text">
                            {isLoading ? "Επεξεργασία..." : (data?.mainSubject || "Ανάλυση")}
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="ai-modal-body">
                {isLoading ? (
                    <div className="ai-loading-container">
                        <div className="typing-dots"><span></span><span></span><span></span></div>
                        <div className="ai-loader-text">Αναμονή απάντησης από AI...</div>
                    </div>
                ) : (
                    <div className="d-flex flex-column">
                        {data?.tags && data.tags.length > 0 && (
                            <div className="d-flex flex-wrap justify-content-center mb-3" style={{ gap: "6px" }}>
                                {data.tags.map((tag, idx) => (
                                    <Badge
                                        key={idx}
                                        style={{
                                            backgroundColor: "#76aeb7",
                                            color: "white",
                                            border: "1px solid #76aeb7",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            padding: "6px 12px"
                                        }}
                                    >
                                        <MdLabel size={14} style={{ marginRight: "5px" }} />
                                        {tag}
                                    </Badge>
                                ))}
                            </div>
                        )}
                        {data && (
                            <div className="text-center mb-2">
                                <Button
                                    size="sm"
                                    variant="link"
                                    onClick={() => setShowAnalysis(!showAnalysis)}
                                    style={{ color: "#6c757d", textDecoration: "none", fontWeight: "600", fontSize: "13.5px" }}
                                >
                                    {showAnalysis ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
                                    {showAnalysis ? " Απόκρυψη Ανάλυσης" : " Προβολή Ανάλυσης"}
                                </Button>
                            </div>
                        )}

                        <Collapse in={showAnalysis}>
                            <div id="analysis-cards-collapse">
                                {data?.summary && (
                                    <div className="ai-summary-card animate-bubble mb-3" style={{ borderLeft: "4px solid #76aeb7" }}>
                                        <h6>Σύνοψη Συνομιλίας</h6>
                                        <p className="m-0" style={{ fontSize: "14.5px", lineHeight: "1.5" }}>
                                            {data.summary}
                                        </p>
                                    </div>
                                )}
                                {data?.keyPoints?.length > 0 && (
                                    <div className="ai-summary-card animate-bubble mb-3">
                                        <h6>Σημαντικά Σημεία</h6>
                                        <ul className="p-0 m-0" style={{ paddingLeft: "20px" }}>
                                            {data.keyPoints.map((point, i) => (
                                                <li key={i} className="ai-key-point mb-1">{point}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {data?.emotionalAnalysis && Object.keys(data.emotionalAnalysis).length > 0 && (
                                    <div className="ai-summary-card animate-bubble mb-3">
                                        <h6>Συναισθηματική Ανάλυση και Τόνος</h6>
                                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                                            {Object.entries(data.emotionalAnalysis).map(([who, sentiment]) => {
                                                const speakerName = data?.speakerNames?.[who] || (who === "0" ? "Πρώτος Ομιλητής" : "Δεύτερος Ομιλητής");
                                                return (
                                                    <div key={who} className="p-2 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                                                        <div style={{ fontWeight: "700", color: "#475569", marginBottom: "4px", fontSize: "13px" }}>
                                                            {speakerName}
                                                        </div>
                                                        <div style={{ fontSize: "14px", color: "#334155", lineHeight: "1.4" }}>
                                                            {sentiment}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Collapse>

                        <div className="ai-toggle-wrapper text-center mt-2 mb-2">
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={handleToggleChat}
                            >
                                {showChat ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
                                {showChat ? " Απόκρυψη Πλήρους Συνομιλίας" : " Προβολή Πλήρους Συνομιλίας"}
                            </Button>
                        </div>

                        <Collapse in={showChat}>
                            <div id="transcript-collapse">
                                <div ref={chatTopRef} style={{ height: "1px", marginTop: "-1px" }}></div>

                                <div className="transcript-collapse-container">
                                    <div className="chat-container pt-3">
                                        {(() => {
                                            const normalizeString = (str) => {
                                                return String(str || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
                                            };
                                            const extractName = (str) => {
                                                if (!str) return null;
                                                const match = String(str).match(/\(([^)]+)\)/);
                                                let extracted = match ? match[1].trim() : null;
                                                if (extracted) extracted = extracted.replace("Unmatched regex expression:", "").trim();
                                                return extracted;
                                            };

                                            const srcNumStr = String(row?.src || "").replace(/\D/g, "");
                                            const isOutbound = srcNumStr.length <= 4 && srcNumStr.length > 0;

                                            let systemAgentName = row?.outbound_cnam;
                                            if (systemAgentName) systemAgentName = String(systemAgentName).replace("Unmatched regex expression:", "").trim();
                                            if (!systemAgentName || systemAgentName.trim() === "") {
                                                systemAgentName = isOutbound ? extractName(row?.src) : extractName(row?.dst);
                                            }
                                            if (!systemAgentName) systemAgentName = "Εκπρόσωπος";

                                            const calcCustomerName = data?.clientIdentifier || row?.clientName || "Πελάτης";
                                            const speakerRoles = {};
                                            if (data?.speakerNames) {
                                                const keys = Object.keys(data.speakerNames);

                                                keys.forEach(key => {
                                                    const normName = normalizeString(data.speakerNames[key]);
                                                    if (normName.includes("εκπροσωπος") || normName.includes("agent")) {
                                                        speakerRoles[key] = "agent";
                                                    } else if (normName.includes("πελατης")) {
                                                        speakerRoles[key] = "customer";
                                                    }
                                                });

                                                keys.forEach(key => {
                                                    if (!speakerRoles[key]) {
                                                        const normName = normalizeString(data.speakerNames[key]);
                                                        const normSysAgent = normalizeString(systemAgentName);
                                                        const normCustomer = normalizeString(calcCustomerName);
                                                        const agentTokens = normSysAgent.split(/[\s-]+/).filter(t => t.length > 2);
                                                        const customerTokens = normCustomer.split(/[\s-]+/).filter(t => t.length > 2);
                                                        const matchesAgent = agentTokens.some(token => normName.includes(token));
                                                        const matchesCustomer = customerTokens.some(token => normName.includes(token));

                                                        if (matchesAgent && !matchesCustomer) {
                                                            speakerRoles[key] = "agent";
                                                        } else if (matchesCustomer && !matchesAgent) {
                                                            speakerRoles[key] = "customer";
                                                        }
                                                    }
                                                });

                                                if (keys.length === 2) {
                                                    const knownRoles = Object.values(speakerRoles);
                                                    if (knownRoles.length === 1) {
                                                        const knownKey = Object.keys(speakerRoles)[0];
                                                        const unknownKey = keys.find(k => k !== knownKey);
                                                        speakerRoles[unknownKey] = speakerRoles[knownKey] === "agent" ? "customer" : "agent";
                                                    }
                                                }
                                            }

                                            return data?.transcript?.map((item, idx) => {
                                                const whoStr = String(item.who);
                                                let isAgent = speakerRoles[whoStr] === "agent";
                                                if (!speakerRoles[whoStr]) {
                                                    isAgent = (item.who === 0);
                                                }
                                                if (row?.ourIdentification === "innerCall") {
                                                    isAgent = true;
                                                }

                                                let displayName = "";
                                                const aiFoundName = data?.speakerNames?.[whoStr];
                                                if (aiFoundName) {
                                                    const cleanAiName = aiFoundName.trim();
                                                    const normCleanAiName = normalizeString(cleanAiName);
                                                    if (/^(πελατης)$/i.test(normCleanAiName)) {
                                                        displayName = calcCustomerName;
                                                    } else if (/^(εκπροσωπος|agent)$/i.test(normCleanAiName)) {
                                                        displayName = systemAgentName;
                                                    } else {
                                                        displayName = aiFoundName;
                                                    }
                                                } else {
                                                    displayName = isAgent ? systemAgentName : calcCustomerName;
                                                }

                                                const typeClass = isAgent ? 'agent' : 'customer';
                                                const hasPrev = idx > 0 && data.transcript[idx - 1].who === item.who;
                                                const hasNext = idx < data.transcript.length - 1 && data.transcript[idx + 1].who === item.who;

                                                let dynamicBorderRadius = "18px";
                                                if (typeClass === 'agent') {
                                                    if (!hasPrev && !hasNext) dynamicBorderRadius = "18px 18px 4px 18px";
                                                    else if (!hasPrev && hasNext) dynamicBorderRadius = "18px 18px 4px 18px";
                                                    else if (hasPrev && hasNext) dynamicBorderRadius = "18px 4px 4px 18px";
                                                    else if (hasPrev && !hasNext) dynamicBorderRadius = "18px 4px 18px 18px";
                                                } else {
                                                    if (!hasPrev && !hasNext) dynamicBorderRadius = "18px 18px 18px 4px";
                                                    else if (!hasPrev && hasNext) dynamicBorderRadius = "18px 18px 18px 4px";
                                                    else if (hasPrev && hasNext) dynamicBorderRadius = "2px 18px 18px 4px";
                                                    else if (hasPrev && !hasNext) dynamicBorderRadius = "2px 18px 18px 18px";
                                                }

                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`chat-bubble-wrapper ${typeClass}`}
                                                        style={{
                                                            marginTop: hasPrev ? '1px' : '16px',
                                                            marginBottom: '0px'
                                                        }}
                                                    >
                                                        {!hasPrev && (
                                                            <span className="chat-role-label">
                                                            {displayName}
                                                        </span>
                                                        )}
                                                        <div
                                                            className={`chat-bubble ${typeClass}`}
                                                            style={{
                                                                borderRadius: dynamicBorderRadius,
                                                                position: 'relative',
                                                                paddingBottom: '20px'
                                                            }}
                                                        >
                                                            {item.msg}
                                                            <span
                                                                className="chat-time-inline"
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '4px',
                                                                    right: '10px',
                                                                    fontSize: '11px',
                                                                    opacity: 0.65,
                                                                    userSelect: 'none'
                                                                }}
                                                            >
                                                            {item.time?.split('.')[0]}
                                                        </span>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="ai-modal-footer">
                {!isLoading && data && (
                    <div className="w-100 d-flex justify-content-between align-items-center ai-identifier">
                        <div className="text-left">
                            <strong>Αναγνωριστικό πελάτη:</strong> {data.clientIdentifier || row?.clientName || "Δεν εντοπίστηκε"}
                        </div>
                        <div className="text-right text-muted" style={{ fontSize: "13px" }}>
                            {(() => {
                                const finalDate = analysisDate;
                                return finalDate ? (
                                    <span>Αναλύθηκε: {moment(finalDate).format("DD/MM/YYYY, HH:mm")}</span>
                                ) : (
                                    <span>Αναλύθηκε: Πρόσφατα</span>
                                );
                            })()}
                            {cost ? <span className="ml-2">AI Κόστος: {getGreekCurrencyFormat(cost)} </span> : ""}
                        </div>
                    </div>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default AICallAnalysisModal;